import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  TablePagination,
  CircularProgress,
  Alert,
  Chip as MuiChip,
  Divider,
  IconButton,
  Tooltip,
  InputAdornment,
  Badge,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  Download,
  Visibility,
  Payment,
  ShoppingCart,
  TrendingUp,
  AttachMoney,
  AccountBalance,
  LocalAtm,
  CreditCard,
  PhoneAndroid,
  Assessment,
  DateRange,
  Clear,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getPurchasePayments } from '../redux/actions/goldsmithPurchaseActions';
import { getActivePaymentModes } from '../redux/actions/paymentModeActions';

const GoldsmithPurchasePaymentList = () => {
  const dispatch = useDispatch();
  const goldsmithPurchaseState = useSelector((state) => state.goldsmithPurchases);
  const paymentModeState = useSelector((state) => state.paymentModes);
  
  const [filters, setFilters] = useState({
    paymentModeId: '',
    startDate: null,
    endDate: null,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('purchase_date');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Defensive programming - provide default values
  const activePaymentModes = paymentModeState?.activePaymentModes || [];
  
  // Defensive programming - provide default values
  const purchasePayments = goldsmithPurchaseState?.purchasePayments || {
    payments: [],
    paymentModeTotals: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
    }
  };
  const loading = goldsmithPurchaseState?.loading || false;
  const error = goldsmithPurchaseState?.error || null;

  // Load data function
  const loadPayments = async () => {
    const filterData = {
      paymentModeId: filters.paymentModeId,
      startDate: filters.startDate ? filters.startDate.toISOString().split('T')[0] : null,
      endDate: filters.endDate ? filters.endDate.toISOString().split('T')[0] : null,
      page: page + 1,
      limit: rowsPerPage,
      search: searchTerm,
      sortBy,
      sortOrder,
    };
    console.log('Loading goldsmith purchase payments with filters:', filterData);
    try {
      await dispatch(getPurchasePayments(filterData));
    } catch (error) {
      console.error('Error loading payments:', error);
    }
  };

  useEffect(() => {
    // Load payment modes and payments
    const loadData = async () => {
      try {
        await dispatch(getActivePaymentModes());
        await loadPayments();
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    
    loadData();
  }, []);

  useEffect(() => {
    loadPayments();
  }, [page, rowsPerPage, sortBy, sortOrder, filters]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '') {
        loadPayments();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
    setPage(0); // Reset to first page when filters change
  };

  const handleSearch = () => {
    loadPayments();
  };

  const handleClearFilters = () => {
    setFilters({
      paymentModeId: '',
      startDate: null,
      endDate: null,
    });
    setSearchTerm('');
    setPage(0);
    loadPayments();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getPaymentModeColor = (mode) => {
    switch (mode) {
      case 'pure_only': return 'success';
      case 'amount_only': return 'warning';
      case 'mixed': return 'info';
      default: return 'default';
    }
  };

  const getPaymentModeIcon = (mode) => {
    switch (mode) {
      case 'pure_only': return <LocalAtm />;
      case 'amount_only': return <AttachMoney />;
      case 'mixed': return <AccountBalance />;
      default: return <Payment />;
    }
  };

  const normalizedPayments = (purchasePayments?.payments && Array.isArray(purchasePayments.payments))
    ? purchasePayments.payments
    : (purchasePayments?.purchases && Array.isArray(purchasePayments.purchases))
      ? purchasePayments.purchases
      : [];

  const filteredPayments = normalizedPayments.filter(payment => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      payment.purchase_number?.toLowerCase().includes(searchLower) ||
      payment.goldsmith?.name?.toLowerCase().includes(searchLower) ||
      payment.payment_mode?.toLowerCase().includes(searchLower)
    );
  });

  const totalAmount = (purchasePayments.paymentModeTotals || []).reduce((sum, total) => sum + (parseFloat(total.total_amount || 0) || 0), 0);
  const totalPurchases = normalizedPayments.length;
  const avgPurchaseAmount = totalPurchases > 0 ? totalAmount / totalPurchases : 0;

  // Show loading only when actively loading and no data yet (mirrors orders page pattern)
  if (loading && (!purchasePayments?.payments || purchasePayments.payments.length === 0)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
            Loading purchase payments...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ 
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Assessment sx={{ fontSize: 'inherit', WebkitTextFillColor: 'unset' }} />
            Goldsmith Purchase Payments
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track and analyze goldsmith purchase payments by payment mode
          </Typography>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                zIndex: 0
              }} />
              <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h6" color="white" gutterBottom>
                      Total Purchases
                    </Typography>
                    <Typography variant="h3" color="white" sx={{ fontWeight: 'bold' }}>
                      {totalPurchases}
                    </Typography>
                  </Box>
                  <ShoppingCart sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                zIndex: 0
              }} />
              <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h6" color="white" gutterBottom>
                      Total Amount
                    </Typography>
                    <Typography variant="h3" color="white" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(totalAmount)}
                    </Typography>
                  </Box>
                  <AttachMoney sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                zIndex: 0
              }} />
              <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h6" color="white" gutterBottom>
                      Payment Modes
                    </Typography>
                    <Typography variant="h3" color="white" sx={{ fontWeight: 'bold' }}>
                      {purchasePayments.paymentModeTotals.length}
                    </Typography>
                  </Box>
                  <Payment sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                zIndex: 0
              }} />
              <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h6" color="white" gutterBottom>
                      Avg. Purchase
                    </Typography>
                    <Typography variant="h3" color="white" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(avgPurchaseAmount)}
                    </Typography>
                  </Box>
                  <TrendingUp sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <FilterList color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Filters & Search
              </Typography>
            </Box>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Payment Mode</InputLabel>
                  <Select
                    value={filters.paymentModeId}
                    onChange={(e) => handleFilterChange('paymentModeId', e.target.value)}
                    label="Payment Mode"
                  >
                    <MenuItem value="">All Payment Modes</MenuItem>
                    <MenuItem value="pure_only">Pure Only</MenuItem>
                    <MenuItem value="amount_only">Amount Only</MenuItem>
                    <MenuItem value="mixed">Mixed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange('startDate', date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange('endDate', date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by purchase number, goldsmith, or payment mode"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<Search />}
                    onClick={handleSearch}
                    size="small"
                    sx={{ minWidth: 100 }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Clear />}
                    onClick={handleClearFilters}
                    size="small"
                    sx={{ minWidth: 100 }}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Payment Mode Totals */}
        {purchasePayments.paymentModeTotals.length > 0 && (
          <Card sx={{ mb: 3, boxShadow: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Assessment color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Payment Mode Summary
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {purchasePayments.paymentModeTotals.map((total) => (
                  <Grid item xs={12} sm={6} md={4} key={total.payment_mode}>
                    <Card sx={{ 
                      border: 1, 
                      borderColor: 'divider',
                      '&:hover': { boxShadow: 2 }
                    }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ 
                            bgcolor: getPaymentModeColor(total.payment_mode) + '.main',
                            width: 40,
                            height: 40
                          }}>
                            {getPaymentModeIcon(total.payment_mode)}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {total.payment_mode?.replace('_', ' ').toUpperCase()}
                            </Typography>
                            <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(total.total_amount)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {total.purchase_count} purchases
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Payments Table */}
        <Card sx={{ boxShadow: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              p: 3,
              borderBottom: 1,
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Purchase Details
                </Typography>
                <Badge badgeContent={filteredPayments.length} color="primary" />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Refresh">
                  <IconButton onClick={loadPayments} size="small" color="primary">
                    <Refresh />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export">
                  <IconButton size="small" color="primary">
                    <Download />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {error ? (
              <Box sx={{ p: 3 }}>
                <Alert severity="error" action={
                  <Button color="inherit" size="small" onClick={loadPayments}>
                    Retry
                  </Button>
                }>
                  {error}
                </Alert>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Purchase Number</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Goldsmith</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Payment Mode</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Net Weight</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Touch</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Gold Rate</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Total Amount</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPayments.map((purchase, index) => (
                        <TableRow 
                          key={purchase.id} 
                          hover
                          sx={{ 
                            '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                            '&:hover': { bgcolor: 'primary.50' }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium" color="primary.main">
                              {purchase.purchase_number || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <DateRange fontSize="small" color="action" />
                              <Typography variant="body2">
                                {formatDate(purchase.purchase_date)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {purchase.goldsmith?.name || 'N/A'}
                              </Typography>
                              {purchase.goldsmith?.phone && (
                                <Typography variant="caption" color="text.secondary">
                                  {purchase.goldsmith.phone}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getPaymentModeIcon(purchase.payment_mode)}
                              label={purchase.payment_mode?.replace('_', ' ').toUpperCase() || 'Unknown'}
                              size="small"
                              color={getPaymentModeColor(purchase.payment_mode)}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              {purchase.net_weight} gms
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {purchase.touch}%
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(purchase.gold_rate)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="primary.main">
                              {formatCurrency(purchase.total_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="View Purchase">
                                <IconButton size="small" color="primary">
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Payment Details">
                                <IconButton size="small" color="secondary">
                                  <Payment />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Purchase Details">
                                <IconButton size="small" color="info">
                                  <ShoppingCart />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredPayments.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <ShoppingCart sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No purchases found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your filters or search criteria
                    </Typography>
                    <Button 
                      variant="outlined" 
                      startIcon={<Clear />}
                      onClick={handleClearFilters}
                      sx={{ mt: 2 }}
                    >
                      Clear Filters
                    </Button>
                  </Box>
                )}

                {/* Pagination */}
                <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
                  <TablePagination
                    component="div"
                    count={purchasePayments.pagination.totalItems || 0}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="Rows per page:"
                    labelDisplayedRows={({ from, to, count }) => 
                      `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                    }
                  />
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default GoldsmithPurchasePaymentList;